<template>
    <div class="home-container fade-in">
        <section class="hero">
            <div class="container">
                <div class="hero-content">
                    <h1>{{ t('home.title') }}</h1>
                    <p class="hero-subtitle">{{ t('home.subtitle') }}</p>

                    <div class="showcase">
                        <div class="example-avatar" v-for="i in 4" :key="i">
                            <img :src="`/examples/example-${i}.png`" :alt="`Example Avatar ${i} - Ugly Avatar Sample`"
                                loading="lazy" />
                        </div>
                    </div>

                    <router-link to="/generator" class="btn btn-primary cta-button">
                        <span>{{ t('home.startCreating') }}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M5 12h14"></path>
                            <path d="m12 5 7 7-7 7"></path>
                        </svg>
                    </router-link>
                </div>
            </div>
        </section>

        <section class="features">
            <div class="container">
                <h2 class="section-title">{{ t('home.features') }}</h2>

                <div class="features-grid">
                    <div class="feature-card card">
                        <div class="feature-icon">🎲</div>
                        <h3>{{ t('home.randomGeneration') }}</h3>
                        <p>{{ t('home.randomGenerationDesc') }}</p>
                    </div>

                    <div class="feature-card card">
                        <div class="feature-icon">💾</div>
                        <h3>{{ t('home.saveCollection') }}</h3>
                        <p>{{ t('home.saveCollectionDesc') }}</p>
                    </div>

                    <div class="feature-card card">
                        <div class="feature-icon">🔄</div>
                        <h3>{{ t('home.easySharing') }}</h3>
                        <p>{{ t('home.easySharingDesc') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="how-it-works">
            <div class="container">
                <div class="card">
                    <h2 class="section-title">{{ t('home.howItWorks') }}</h2>

                    <div class="steps">
                        <div class="step">
                            <div class="step-number">1</div>
                            <div class="step-content">
                                <h3>{{ t('home.chooseOptions') }}</h3>
                                <p>{{ t('home.goToGenerator') }}</p>
                            </div>
                        </div>

                        <div class="step">
                            <div class="step-number">2</div>
                            <div class="step-content">
                                <h3>{{ t('home.generateAvatar') }}</h3>
                                <p>{{ t('home.generateDesc') }}</p>
                            </div>
                        </div>

                        <div class="step">
                            <div class="step-number">3</div>
                            <div class="step-content">
                                <h3>{{ t('home.customizeIt') }}</h3>
                                <p>{{ t('home.customizeDesc') }}</p>
                            </div>
                        </div>

                        <div class="step">
                            <div class="step-number">4</div>
                            <div class="step-content">
                                <h3>{{ t('home.saveAndShare') }}</h3>
                                <p>{{ t('home.saveShareDesc') }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="cta-container">
                        <router-link to="/generator" class="btn btn-primary">{{ t('home.createNow') }}</router-link>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { computed, onMounted } from 'vue'
import { logEvent } from 'firebase/analytics'
import { analytics } from '../firebase'

export default {
    name: 'Home',
    setup() {
        const { t } = useI18n()

        onMounted(() => {
            // 记录主页访问事件
            logEvent(analytics, 'screen_view', {
                screen_name: 'home'
            });
        });

        return { t }
    },
    metaInfo() {
        return {
            title: this.t('home.title') + ' - Ugly-Cute Avatar Generator',
            meta: [
                { name: 'description', content: this.t('home.subtitle') }
            ]
        }
    }
}
</script>

<style scoped>
.home-container {
    padding-bottom: 4rem;
}

.hero {
    padding: 5rem 0 3rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    background: linear-gradient(120deg, #f8f9fa, #e9ecef);
}

.hero::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: radial-gradient(rgba(0, 0, 0, 0.03) 2px, transparent 2px);
    background-size: 30px 30px;
    z-index: 0;
}

.hero-content {
    position: relative;
    z-index: 1;
}

.hero h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--secondary-color);
    font-weight: 700;
}

.hero-subtitle {
    font-size: 1.25rem;
    color: var(--text-secondary);
    margin-bottom: 2.5rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.showcase {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-bottom: 3rem;
}

.example-avatar {
    width: 150px;
    height: 150px;
    border-radius: var(--border-radius-lg);
    overflow: hidden;
    box-shadow: var(--box-shadow);
    transition: var(--transition);
    transform: rotate(0deg);
}

.example-avatar:hover {
    transform: translateY(-5px) rotate(3deg);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.example-avatar:nth-child(even):hover {
    transform: translateY(-5px) rotate(-3deg);
}

.example-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.cta-button {
    font-size: 1.2rem;
    padding: 0.9rem 2rem;
    border-radius: 50px;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
}

.cta-button:hover {
    color: var(--text-light);
    /* 确保悬停时文字保持白色 */
}

.cta-button svg {
    transition: transform 0.3s ease;
}

.cta-button:hover svg {
    transform: translateX(4px);
}

.section-title {
    text-align: center;
    margin-bottom: 3rem;
    font-size: 2.2rem;
    color: var(--secondary-color);
    position: relative;
    display: inline-block;
}

.section-title::after {
    content: '';
    position: absolute;
    height: 4px;
    width: 60px;
    background-color: var(--primary-color);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
}

.features {
    padding: 5rem 0;
    text-align: center;
}

.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
}

.feature-card {
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.feature-icon {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    background-color: rgba(66, 185, 131, 0.1);
    width: 80px;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    margin-bottom: 1.5rem;
}

.feature-card h3 {
    margin-bottom: 1rem;
    color: var(--secondary-color);
    font-size: 1.4rem;
}

.feature-card p {
    color: var(--text-secondary);
    line-height: 1.6;
}

.how-it-works {
    padding: 3rem 0 5rem;
}

.how-it-works .card {
    padding: 3rem;
}

.steps {
    margin: 3rem 0;
}

.step {
    display: flex;
    margin-bottom: 2rem;
    align-items: flex-start;
}

.step-number {
    width: 40px;
    height: 40px;
    background-color: var(--primary-color);
    color: white;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    font-size: 1.2rem;
    margin-right: 1.5rem;
    flex-shrink: 0;
}

.step-content h3 {
    margin-bottom: 0.5rem;
    color: var(--secondary-color);
}

.step-content p {
    color: var(--text-secondary);
}

.cta-container {
    text-align: center;
    margin-top: 3rem;
}

@media (max-width: 768px) {
    .hero h1 {
        font-size: 2.2rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
    }

    .example-avatar {
        width: 120px;
        height: 120px;
    }

    .how-it-works .card {
        padding: 2rem 1.5rem;
    }

    .step {
        flex-direction: column;
    }

    .step-number {
        margin-bottom: 1rem;
    }
}
</style>
